import { jwtDecode } from 'jwt-decode'

/**
 * Decodifica un JWT sin verificar la firma (solo para leer el payload)
 */
export function decodeJWT(token) {
  if (!token) return null

  try {
    return jwtDecode(token)
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

/**
 * Verifica si un token está expirado
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Segundos de buffer antes de la expiración (default: 60)
 * @returns {boolean} - true si está expirado o a punto de expirar
 */
export function isTokenExpired(token, bufferSeconds = 60) {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return true

  const now = Math.floor(Date.now() / 1000)
  const expiresAt = decoded.exp - bufferSeconds

  return now >= expiresAt
}

/**
 * Obtiene el tiempo restante hasta que expire el token (en segundos)
 * @param {string} token - JWT token
 * @returns {number} - Segundos hasta la expiración, o 0 si ya expiró
 */
export function getTokenTimeRemaining(token) {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return 0

  const now = Math.floor(Date.now() / 1000)
  const remaining = decoded.exp - now

  return remaining > 0 ? remaining : 0
}

/**
 * Verifica si un token necesita ser refrescado
 * Refresca si queda menos del 20% del tiempo de vida o menos de 5 minutos
 * @param {string} token - JWT token
 * @returns {boolean}
 */
export function shouldRefreshToken(token) {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp || !decoded.iat) return true

  const now = Math.floor(Date.now() / 1000)
  const totalLifetime = decoded.exp - decoded.iat
  const timeRemaining = decoded.exp - now

  if (timeRemaining < 300) return true

  const percentageRemaining = timeRemaining / totalLifetime
  if (percentageRemaining < 0.2) return true

  return false
}

/**
 * Obtiene información sobre el estado del token
 * @param {string} token - JWT token
 * @returns {object} - Información del token
 */
export function getTokenInfo(token) {
  const decoded = decodeJWT(token)
  if (!decoded) {
    return {
      valid: false,
      expired: true,
      timeRemaining: 0,
      shouldRefresh: true,
    }
  }

  const now = Math.floor(Date.now() / 1000)
  const timeRemaining = decoded.exp ? decoded.exp - now : 0
  const expired = timeRemaining <= 0

  return {
    valid: true,
    expired,
    timeRemaining: timeRemaining > 0 ? timeRemaining : 0,
    shouldRefresh: shouldRefreshToken(token),
    payload: decoded,
  }
}
